import { useLiveQuery } from 'dexie-react-hooks'
import type { LocationMode } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'

/** Location at the site (M21, the owner's answer of 2026-10-23: no GPS): whether the app may take this phone's position at all is the site's setting,
 *  `core.site.location_mode`, read from the device's mirror of the site row (works offline). It is OFF unless the club turned it on: a site row from
 *  before the setting existed, a row not loaded yet, a value this build does not know, all read as off, so nothing ever asks the phone for its
 *  position by default. Off, the app never calls the browser's geolocation nor starts the native watcher (no permission prompt either): no sharing, no
 *  banner, no live layer, no "Sur le parcours", no "use my position" in the map editors, and "Me guider" becomes "Voir sur la carte" (the area on the
 *  map, no position, no distance, no direction). The server holds the same rule whatever this device does (LOCATION_OFF, migration 20261123100100). */
export function locationModeOf(site: { location_mode?: unknown } | null | undefined): LocationMode {
  return site?.location_mode === 'on' ? 'on' : 'off'
}

/** The site's location mode, from the mirror (off until the row says on). */
export function useLocationMode(): LocationMode {
  const { db, site } = useClub()
  const row = useLiveQuery(() => (site?.site_id ? db.site.get(site.site_id) : undefined), [db, site?.site_id]) as { location_mode?: unknown } | undefined
  return locationModeOf(row)
}

/** Is location on at this site? (false while the mirror has not said so) */
export const useLocationOn = (): boolean => useLocationMode() === 'on'
